/**
 * Streaming Aura TTS client — the duplex endgame (task 31). One WebSocket
 * per spoken answer against the companion speak-stream proxy: sentences go
 * up as {Speak}/{Flush}, raw linear16 PCM frames come back and play through
 * a Web Audio sink as they arrive. First audio lands about one sentence-decode plus
 * ~300ms after the question, and barge-in is the duplex cancellation order:
 * clear the sink (worklet), send Clear (NEVER Close), keep the socket.
 *
 * Same facade as createCompanionSpeech ({onDelta, onDone, cancel}), so the
 * voice session can swap transports without changing call sites. WS and
 * sink are injectable; jsdom tests drive the protocol with fakes.
 */
import { COMPANION_CHAT_API } from '@/config/constants'
import { useAccountStore } from '@/stores/account-store'
import { SentenceSegmenter, stripSpokenMarkdown } from './sentence-tts-pipeline'

export const SPEAK_SAMPLE_RATE = 24000

/** Minimal WS surface (browser WebSocket compatible). */
export interface SpeakSocket {
  send(data: string): void
  close(): void
  binaryType: string
  onopen: ((ev?: unknown) => void) | null
  onmessage: ((ev: { data: unknown }) => void) | null
  onclose: ((ev?: unknown) => void) | null
  onerror: ((ev?: unknown) => void) | null
}

/** PCM playback sink; the real one is a Web Audio player, tests use fakes. */
export interface PcmSink {
  /** Queue one linear16 frame for gapless playback. */
  write(frame: ArrayBuffer): void
  /** Barge-in: stop and drop everything queued. */
  clear(): void
  /** Resolves when everything written so far has finished playing. */
  drain(): Promise<void>
}

export interface AuraSpeechOptions {
  language?: string | null
  voice?: string | null
  onError?(error: unknown): void
  /** Test seams. */
  socketFactory?(url: string, protocols: string[]): SpeakSocket
  sink?: PcmSink
  tokenProvider?(): string | null
}

/** WS auth rides the bearer subprotocol, so this needs the raw access token. */
function defaultTokenProvider(): string | null {
  return useAccountStore.getState().session?.tokens.accessToken ?? null
}

function speakStreamUrl(opts: AuraSpeechOptions): string {
  const base = COMPANION_CHAT_API.replace(/^http/, 'ws')
  const params = new URLSearchParams()
  if (opts.language) params.set('language', opts.language)
  if (opts.voice) params.set('voice', opts.voice)
  const qs = params.toString()
  return `${base}/speak-stream${qs ? `?${qs}` : ''}`
}

/**
 * Open a speak-stream session. Sentences are segmented from streamed
 * deltas; each is sent as Speak, with a Flush after the final one so the
 * server pushes out the tail audio.
 */
export function createAuraSpeech(opts: AuraSpeechOptions = {}) {
  const segmenter = new SentenceSegmenter()
  const token = (opts.tokenProvider ?? defaultTokenProvider)()
  const protocols = token ? ['bearer', token] : []
  const socket: SpeakSocket = opts.socketFactory
    ? opts.socketFactory(speakStreamUrl(opts), protocols)
    : (new WebSocket(speakStreamUrl(opts), protocols) as unknown as SpeakSocket)
  socket.binaryType = 'arraybuffer'

  const sink = opts.sink ?? null
  if (!sink) throw new Error('createAuraSpeech requires a PcmSink (see pcm-player.ts)')

  let open = false
  let cancelled = false
  let done = false
  let flushed = false
  const outbox: string[] = []
  let resolveFinished: () => void
  const finished = new Promise<void>((resolve) => {
    resolveFinished = resolve
  })

  const send = (msg: Record<string, unknown>) => {
    const frame = JSON.stringify(msg)
    if (open) socket.send(frame)
    else outbox.push(frame)
  }

  const maybeFinish = () => {
    if (!done || !flushed) return
    void sink.drain().then(() => resolveFinished())
  }

  socket.onopen = () => {
    open = true
    for (const frame of outbox.splice(0)) socket.send(frame)
  }
  socket.onmessage = (ev) => {
    if (cancelled) return
    if (ev.data instanceof ArrayBuffer) {
      sink.write(ev.data)
      return
    }
    try {
      const msg = JSON.parse(String(ev.data)) as { type?: string }
      if (msg.type === 'Flushed') {
        flushed = true
        maybeFinish()
      }
    } catch {
      /* non-JSON text frame — ignore */
    }
  }
  socket.onerror = (e) => {
    opts.onError?.(e)
  }
  socket.onclose = () => {
    // Socket gone (upstream close or network drop): nothing further will
    // arrive, so resolve on whatever audio made it into the sink.
    flushed = true
    done = true
    maybeFinish()
  }

  const speakSentence = (sentence: string) => {
    const text = stripSpokenMarkdown(sentence).trim()
    if (text) send({ type: 'Speak', text })
  }

  return {
    onDelta(text: string): void {
      if (cancelled) return
      for (const sentence of segmenter.push(text)) speakSentence(sentence)
    },
    onDone(): void {
      if (cancelled) return
      for (const sentence of segmenter.flush()) speakSentence(sentence)
      send({ type: 'Flush' })
      done = true
      maybeFinish()
    },
    /** Duplex barge-in order: clear the sink, Clear the stream, keep the socket. */
    cancel(): void {
      cancelled = true
      sink.clear()
      send({ type: 'Clear' })
      resolveFinished()
    },
    /** End the session entirely (answer finished or surface closed). */
    close(): void {
      socket.close()
    },
    finished,
  }
}
